import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Base, Big } from '../Typography';
import { SENSATION_WHITE } from '../colors';
import { SHADOW_DEFAULT, SHADOW_FOCUSED } from '../shadows';
import AlertIcon from '@tourlane/iconography/Glyphs/Notifications/Alert';
import InfoIcon from '@tourlane/iconography/Glyphs/Notifications/Info';
import WarningIcon from '@tourlane/iconography/Glyphs/Notifications/Warning';
import SuccessIcon from '@tourlane/iconography/Glyphs/Notifications/Success';
import SvgIcon from '../SvgIcon';
import IconButton from '../Button/IconButton';
import InfoButton from '../Info/InfoButton';
import { getPx } from '../helpers';
import { prevent } from '../helpers';
import { useDeviceType } from '../hooks';
import { NOTIFICATION_BORDER_HEIGHT, NOTIFICATION_BORDER_RADIUS, NOTIFICATION_BORDER_WIDTH, NOTIFICATION_CLOSE_BUTTON_SIZE, NOTIFICATION_CONDENSED_HORIZONTAL_PADDING, NOTIFICATION_DELAY, NOTIFICATION_ICON_LEFT_POSITION, NOTIFICATION_ICON_SIZE, NOTIFICATION_ICON_TOP_POSITION, NOTIFICATION_MARGIN, NOTIFICATION_PADDING, NOTIFICATION_WIDTH } from './constants';
import { EMPTY_PROPS } from '../helpers';
import { Big as BigTypographySize } from '../Typography/constants/big.size';
import { absoluteCenteredStyle } from '../tokens/centered';
import { getVariantColor } from '../helpers/variants';
import { height, width, left, top, padding, paddingRight, marginBottom } from '../style';
const ICONS = {
  default: InfoIcon,
  info: InfoIcon,
  warning: WarningIcon,
  alert: AlertIcon,
  success: SuccessIcon
};
const getIcon = (variant = 'default') => ICONS[variant] || ICONS.default;
const Container = styled.div`
  position: relative;
  overflow: hidden;
  background-color: ${SENSATION_WHITE};
  border-radius: ${getPx(NOTIFICATION_BORDER_RADIUS)};
  box-shadow: ${SHADOW_DEFAULT};
  ${padding(NOTIFICATION_PADDING)}
  ${({
  hasClose
}) => !hasClose && paddingRight(NOTIFICATION_CONDENSED_HORIZONTAL_PADDING)}
  &::after {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    bottom: 0;
    background-color: ${({
  color,
  variant
}) => color ?? getVariantColor(variant)};
  }

  ${({
  borderLocation
}) => borderLocation === 'bottom' ? css`
          &:after {
            width: 100%;
            ${height(NOTIFICATION_BORDER_HEIGHT)}
          }
        ` : css`
          &::after {
            top: 0;
            ${width(NOTIFICATION_BORDER_WIDTH)}
          }
        `}
`;
const Wrapper = styled(InfoButton)`
  ${({
  $width
}) => width($width ?? NOTIFICATION_WIDTH)}
  ${marginBottom(NOTIFICATION_MARGIN)}
  
  ${({
  $hasTouch
}) => !$hasTouch && `
    ${Container} {
      transition: box-shadow 0.3s ease-in-out;
    }
    &:hover {
      ${Container} {
        box-shadow: ${SHADOW_FOCUSED};
      }
    }
  `}
`;
const EmojiWrapper = styled(Big)`
  display: block;
  margin-top: 1px;
`;
const CloseButtonPlaceholder = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
`;
const IconPlaceholder = styled.div`
  position: absolute;
  ${left(NOTIFICATION_ICON_LEFT_POSITION)}
  ${top(NOTIFICATION_ICON_TOP_POSITION)}
  ${width(NOTIFICATION_ICON_SIZE)}
  ${height(BigTypographySize.lineHeight)}
  & > * {
    ${absoluteCenteredStyle}
  }
`;
const NotificationBlock = ({
  variant = 'default',
  title,
  message,
  icon,
  emoji,
  delay = NOTIFICATION_DELAY,
  onClose,
  onAutoClose,
  color,
  width,
  closeButtonLabel = 'Close',
  closeButtonProps = EMPTY_PROPS,
  borderDirection = 'left',
  ...props
}) => {
  const Icon = getIcon(variant);

  // set timer to call onAutoClose when delay passes
  useEffect(() => {
    if (!delay) {
      return;
    }
    const timeoutId = setTimeout(() => onAutoClose?.(), delay);
    return () => clearTimeout(timeoutId);
  }, [delay]);
  const {
    hasTouchScreen
  } = useDeviceType();
  return /*#__PURE__*/React.createElement(Wrapper, _extends({
    $hasTouch: hasTouchScreen,
    $width: width,
    fullWidth: true
  }, props), /*#__PURE__*/React.createElement(Container, {
    borderLocation: borderDirection,
    color: color,
    variant: variant,
    hasClose: onClose
  }, /*#__PURE__*/React.createElement(Big, {
    strong: true,
    ellipsis: true
  }, title), message && /*#__PURE__*/React.createElement(Base, {
    wordBreak: 'break-word'
  }, message)), /*#__PURE__*/React.createElement(IconPlaceholder, null, emoji ? /*#__PURE__*/React.createElement(EmojiWrapper, null, emoji) : /*#__PURE__*/React.createElement(SvgIcon, {
    size: NOTIFICATION_ICON_SIZE,
    color: color ?? getVariantColor(variant)
  }, icon || /*#__PURE__*/React.createElement(Icon, null))), onClose && /*#__PURE__*/React.createElement(CloseButtonPlaceholder, null, /*#__PURE__*/React.createElement(IconButton, _extends({
    tabIndex: -1,
    hasClose: true,
    onClick: e => {
      prevent(e);
      onClose();
    },
    size: NOTIFICATION_CLOSE_BUTTON_SIZE,
    "aria-label": closeButtonLabel
  }, closeButtonProps))));
};
export default NotificationBlock;